import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import { IDeviceTableRow } from "../../hooks/useDeviceTableRows";
import ChipFilledPrimary from "../../../../common/components/chip/ChipFilledPrimary";
import ChipFilledOther from "../../../../common/components/chip/ChipFilledOther";
import {
  getSKUDisplayLabel,
  isSKUAssigned,
} from "../../../../common/variables/skuOptions";

interface DeviceDetailViewModeProps {
  deviceDatum: IDeviceTableRow;
}

const DeviceDetailViewMode = ({
  deviceDatum,
}: DeviceDetailViewModeProps): JSX.Element => {
  const { getCachedLocation } = useLocationFromCache();
  const locationCache = getCachedLocation(deviceDatum.location.id ?? "");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Table
        size="small"
        sx={{
          "& td": {
            padding: "8px 0px",
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                DEVICE STATUS
              </Typography>
            </TableCell>

            <TableCell sx={{ border: 0, width: "500px", whiteSpace: "nowrap" }}>
              {deviceDatum.isOnline ? (
                <ChipFilledPrimary label="Online" />
              ) : (
                <ChipFilledOther label="Offline" />
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                SKU
              </Typography>
            </TableCell>

            <TableCell
              sx={{
                border: 0,
                width: "500px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Typography variant="body2Regular" component="span" noWrap>
                {isSKUAssigned(deviceDatum?.skuNumber)
                  ? getSKUDisplayLabel(deviceDatum?.skuNumber)
                  : ""}
              </Typography>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                PRODUCT NAME
              </Typography>
            </TableCell>

            <TableCell sx={{ border: 0, width: "500px", whiteSpace: "nowrap" }}>
              <Typography variant="body2Regular" component="span" noWrap>
                {deviceDatum?.skuName}
              </Typography>
            </TableCell>
          </TableRow>

          {deviceDatum?.serialNumber && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  SERIAL NUMBER (S/N)
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  border: 0,
                  width: "500px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography variant="body2Regular" component="span" noWrap>
                  {deviceDatum.serialNumber}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {deviceDatum?.componentSerialNumber && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  COMPONENT S/N
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  border: 0,
                  width: "500px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography variant="body2Regular" component="span" noWrap>
                  {deviceDatum.componentSerialNumber}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                DEVICE MODEL
              </Typography>
            </TableCell>

            <TableCell
              sx={{
                border: 0,
                width: "500px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Typography variant="body2Regular" component="span" noWrap>
                {deviceDatum?.makeModelId}
              </Typography>
            </TableCell>
          </TableRow>

          {deviceDatum?.deviceData && deviceDatum?.deviceData?.sourceVideo && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  RTSP SOURCE
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  border: 0,
                  width: "500px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <Typography variant="body2Regular" component="span">
                  {deviceDatum?.deviceData?.sourceVideo}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {deviceDatum?.deviceData && deviceDatum?.deviceData?.rtspHost && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  RTSP CLIENT
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  border: 0,
                  width: "500px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography variant="body2Regular" component="span">
                  {deviceDatum?.deviceData?.rtspHost}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {deviceDatum?.deviceData && deviceDatum?.deviceData?.hasPanTilt && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  PAN/TILT
                </Typography>
              </TableCell>

              <TableCell
                sx={{ border: 0, width: "500px", whiteSpace: "nowrap" }}
              >
                <Typography variant="body2Regular" component="span">
                  {deviceDatum?.deviceData?.hasPanTilt ? "Yes" : "No"}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {deviceDatum?.deviceData && deviceDatum?.deviceData?.panTiltType && (
            <TableRow>
              <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
                <Typography variant="inputLabel" component="span" noWrap>
                  PT TYPE
                </Typography>
              </TableCell>
              <TableCell
                sx={{ border: 0, width: "500px", whiteSpace: "nowrap" }}
              >
                <Typography variant="body2Regular" component="span">
                  {deviceDatum?.deviceData?.panTiltType}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                LOCATION
              </Typography>
            </TableCell>

            <TableCell
              sx={{
                border: 0,
                width: "500px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Typography variant="body2Regular" component="span" noWrap>
                {locationCache?.name}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell variant="head" sx={{ border: 0, width: "140px" }}>
              <Typography variant="inputLabel" component="span" noWrap>
                NODE ID
              </Typography>
            </TableCell>

            <TableCell
              sx={{
                border: 0,
                width: "500px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Typography variant="body2Regular" component="span">
                {deviceDatum?.node.name}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default DeviceDetailViewMode;
